import { useState } from 'react'
import { Mail, Lock, ShieldCheck, Handshake, TrendingUp, BarChart3, Users } from 'lucide-react'
import { supabase } from '../supabase'

const LOGO_SRC = '/logo-delivr.svg'

export default function Auth() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  async function handleSubmit() {
    if (!email || !password) return alert('Please enter your email and password')
    setLoading(true)
    setMessage('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setMessage(error.message)
    setLoading(false)
  }

  async function handleForgotPassword() {
    if (!email) { setMessage('Enter your email address first, then click forgot password.'); return }
    setLoading(true)
    setMessage('')
    const { error } = await supabase.auth.resetPasswordForEmail(email)
    if (error) { setMessage(error.message) } else { setMessage('Password reset link sent if this account exists.') }
    setLoading(false)
  }

  return (
    <div className="delivr-auth-page">
      <style>{`
        * { box-sizing: border-box; }

        .delivr-auth-page {
          height: 100vh;
          max-height: 100vh;
          overflow: hidden;
          display: grid;
          grid-template-columns: 1fr 1fr;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          color: #070B24;
        }

        /* ── Brand panel ── */
        .delivr-brand-panel {
          position: relative;
          overflow: hidden;
          padding: 48px 56px;
          background:
            radial-gradient(circle at 15% 95%, rgba(72,79,255,0.65), transparent 34%),
            radial-gradient(circle at 80% 20%, rgba(59,130,246,0.32), transparent 28%),
            linear-gradient(145deg, #030720 0%, #061047 48%, #111B78 100%);
          color: #fff;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .delivr-brand-panel::after {
          content: '';
          position: absolute;
          right: -190px; bottom: -210px;
          width: 600px; height: 600px;
          background: rgba(67,86,255,0.17);
          clip-path: polygon(0 20%,58% 20%,72% 34%,48% 58%,72% 82%,58% 96%,0 96%,24% 58%);
        }

        .delivr-brand-inner {
          position: relative;
          z-index: 1;
          max-width: 620px;
        }

        /* Logo hero — large but negative margins eat the PNG's transparent padding */
        .delivr-logo-row { margin-bottom: 20px; }

.delivr-logo-hero img {
  filter: brightness(0) invert(1);
  width: 280px;
  height: auto;
  display: block;
  margin-bottom: 20px;
}

        .delivr-brand-title {
          font-size: 32px;
          line-height: 1.2;
          letter-spacing: -1.2px;
          font-weight: 850;
          max-width: 500px;
          margin-bottom: 12px;
        }

        .delivr-brand-copy {
          font-size: 15px;
          line-height: 1.55;
          color: rgba(255,255,255,0.82);
          max-width: 520px;
          margin-bottom: 24px;
        }

        .delivr-feature-list { display: grid; gap: 14px; }

        .delivr-feature {
          display: grid;
          grid-template-columns: 44px 1fr;
          gap: 14px;
          align-items: center;
        }

        .delivr-feature-icon {
          width: 44px; height: 44px;
          border-radius: 10px;
          background: rgba(79,100,255,0.25);
          border: 1px solid rgba(255,255,255,0.08);
          color: #5B8CFF;
          display: flex; align-items: center; justify-content: center;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
          flex-shrink: 0;
        }

        .delivr-feature-title { font-size: 14px; font-weight: 800; margin-bottom: 2px; }
        .delivr-feature-copy  { font-size: 12.5px; color: rgba(255,255,255,0.72); }

        /* ── Login panel ── */
        .delivr-login-panel {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px;
          background:
            radial-gradient(circle at 80% 15%, rgba(79,70,229,0.08), transparent 30%),
            #F8FAFC;
        }

        .delivr-login-card {
          width: 100%;
          max-width: 560px;
          background: rgba(255,255,255,0.96);
          border: 1px solid rgba(226,232,240,0.9);
          border-radius: 20px;
          padding: 40px 52px 36px;
          box-shadow: 0 30px 80px rgba(15,23,42,0.12);
        }

        /* Logo card — large, negative margins cancel PNG padding */
        .delivr-card-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
        }

.delivr-logo-card img {
  width: 200px;
  height: auto;
  display: block;
  margin: 0 auto 20px;
}

        .delivr-login-title {
          font-size: 22px;
          font-weight: 850;
          letter-spacing: -0.6px;
          margin-bottom: 4px;
        }

        .delivr-login-subtitle {
          font-size: 14px;
          color: #64748B;
          margin-bottom: 22px;
        }

        .delivr-field { margin-bottom: 14px; }

        .delivr-label {
          display: block;
          font-size: 12.5px;
          font-weight: 800;
          color: #111827;
          margin-bottom: 6px;
        }

        .delivr-input-wrap { position: relative; }

        .delivr-input-icon {
          position: absolute; left: 14px; top: 50%;
          transform: translateY(-50%);
          color: #64748B; display: flex; align-items: center; pointer-events: none;
        }

        .delivr-input {
          width: 100%;
          height: 46px;
          border: 1px solid #D7DEE9;
          border-radius: 9px;
          padding: 0 14px 0 44px;
          font-size: 14.5px;
          color: #0F172A;
          outline: none;
          background: #fff;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .delivr-input:focus {
          border-color: #4F46E5;
          box-shadow: 0 0 0 4px rgba(79,70,229,0.10);
        }

        .delivr-input::placeholder { color: #94A3B8; }

        .delivr-message {
          border-radius: 8px; padding: 10px 12px;
          font-size: 12.5px; font-weight: 700; margin-bottom: 14px;
          background: #FEF2F2; color: #B91C1C; border: 1px solid #FECACA;
        }
        .delivr-message.success { background: #ECFDF5; color: #047857; border-color: #A7F3D0; }

        .delivr-submit {
          width: 100%; height: 50px; border: none; border-radius: 9px;
          background: linear-gradient(135deg, #060A25, #11195A);
          color: white; font-size: 15px; font-weight: 850; cursor: pointer;
          box-shadow: 0 14px 30px rgba(15,23,42,0.20);
          transition: transform 0.15s ease, opacity 0.15s ease;
        }
        .delivr-submit:hover:not(:disabled) { transform: translateY(-1px); }
        .delivr-submit:disabled { opacity: 0.65; cursor: not-allowed; }

        .delivr-forgot {
          margin-top: 16px; text-align: center;
          font-size: 13px; font-weight: 700; color: #304CFF; cursor: pointer;
        }

        .delivr-card-divider {
          display: grid; grid-template-columns: 1fr auto 1fr;
          align-items: center; gap: 12px; color: #64748B; margin: 20px 0 16px;
        }
        .delivr-card-divider::before, .delivr-card-divider::after {
          content: ''; height: 1px; background: #E2E8F0;
        }

        .delivr-access-note {
          text-align: center; font-size: 12.5px; color: #64748B; line-height: 1.5;
        }

        @media (max-width: 1050px) {
          .delivr-auth-page { grid-template-columns: 1fr; height: auto; max-height: none; overflow: auto; }
          .delivr-brand-panel { display: none; }
          .delivr-login-panel { min-height: 100vh; }
        }

        @media (max-width: 620px) {
          .delivr-login-panel { padding: 20px; }
          .delivr-login-card { padding: 28px 20px; }
          .delivr-logo-card img { width: 220px; margin: -24px auto -24px; }
        }
      `}</style>

      <section className="delivr-brand-panel">
        <div className="delivr-brand-inner">
          <div className="delivr-logo-row">
            <div className="delivr-logo-hero">
              <img src={LOGO_SRC} alt="Delivr" />
            </div>
          </div>
          <h1 className="delivr-brand-title">Deliver sponsorship commitments with confidence.</h1>
          <p className="delivr-brand-copy">
            Delivr is the partnership delivery management platform built for rights holders and brands to manage, track and report with confidence.
          </p>
          <div className="delivr-feature-list">
            <Feature icon={<Handshake size={22} />} title="Rights & Inventory Management" copy="Centralise agreements, assets and rights information." />
            <Feature icon={<TrendingUp size={22} />} title="Delivery Tracking & Progress" copy="Track activations, deadlines and commitments in real time." />
            <Feature icon={<BarChart3 size={22} />} title="Client Reporting & Insights" copy="Create reports that prove partnership value." />
            <Feature icon={<Users size={22} />} title="Team Collaboration & Notes" copy="Keep teams aligned around every deal and update." />
          </div>
        </div>
      </section>

      <section className="delivr-login-panel">
        <div className="delivr-login-card">
          <div className="delivr-card-logo">
            <div className="delivr-logo-card">
              <img src={LOGO_SRC} alt="Delivr" />
            </div>
          </div>
          <div className="delivr-login-title">Sign in to your account</div>
          <div className="delivr-login-subtitle">Welcome back. Please enter your details.</div>

          <div className="delivr-field">
            <label className="delivr-label">Email</label>
            <div className="delivr-input-wrap">
              <span className="delivr-input-icon"><Mail size={17} /></span>
              <input className="delivr-input" type="email" placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
            </div>
          </div>

          <div className="delivr-field">
            <label className="delivr-label">Password</label>
            <div className="delivr-input-wrap">
              <span className="delivr-input-icon"><Lock size={16} /></span>
              <input className="delivr-input" type="password" placeholder="Enter your password"
                value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
            </div>
          </div>

          {message && (
            <div className={`delivr-message ${message.toLowerCase().includes('sent') ? 'success' : ''}`}>
              {message}
            </div>
          )}

          <button className="delivr-submit" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="delivr-forgot" onClick={handleForgotPassword}>Forgot your password?</div>

          <div className="delivr-card-divider"><ShieldCheck size={20} /></div>

          <div className="delivr-access-note">Need access? Contact your Delivr administrator.</div>
        </div>
      </section>
    </div>
  )
}

function Feature({ icon, title, copy }) {
  return (
    <div className="delivr-feature">
      <div className="delivr-feature-icon">{icon}</div>
      <div>
        <div className="delivr-feature-title">{title}</div>
        <div className="delivr-feature-copy">{copy}</div>
      </div>
    </div>
  )
}
